import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useClassifier } from "@/hooks/useClassifier";
import type { LlmModelId } from "@smartmed/llm-models";
import { cn } from "@/lib/utils";
import type { CoMorbiditiesStepResult } from "@/types/classification";
import { Check, ChevronDown, ChevronRight, FileText, Repeat } from "lucide-react";
import { useEffect, useState } from "react";
import DebugInfoJsonViewer from "./DebugInfoJsonViewer";

// Inline PromptDialog for reuse
const PromptDialog = ({
  open,
  onClose,
  prompt,
  rawOutput,
}: {
  open: boolean;
  onClose: () => void;
  prompt: string;
  rawOutput?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (prompt) {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <div
      className={cn("fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30", !open && "hidden")}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-zinc-900 rounded-lg shadow-lg max-w-2xl w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Prompt & Raw Output</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            ×
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {/* Prompt column */}
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2">
              <div className="font-semibold mb-2">Prompt</div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 ml-1"
                onClick={handleCopy}
                disabled={copied}
                aria-label={copied ? "Copied" : "Copy prompt to clipboard"}
                title={copied ? "Copied" : "Copy prompt to clipboard"}
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <FileText className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {prompt ? (
                <pre className="bg-muted p-3 rounded text-sm whitespace-pre-wrap overflow-x-auto h-full">{prompt}</pre>
              ) : (
                <div className="text-muted-foreground">No prompt available</div>
              )}
            </div>
          </div>
          {/* Raw Output column */}
          <div className="flex flex-col h-full">
            <div className="font-semibold mb-2">Raw Output</div>
            <div className="flex-1 min-h-0 overflow-auto">
              {rawOutput ? (
                <pre className="bg-muted p-3 rounded text-sm whitespace-pre-wrap overflow-x-auto h-full">
                  {rawOutput}
                </pre>
              ) : (
                <div className="text-muted-foreground">No raw output available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ReclassifyCoMorbiditiesDialog for replay action
const ReclassifyCoMorbiditiesDialog = ({
  open,
  onClose,
  medicalText,
  diagnoses,
  llmModelId,
}: {
  open: boolean;
  onClose: () => void;
  medicalText: string | null;
  diagnoses: string[];
  llmModelId?: LlmModelId | null;
}) => {
  const { classifyCoMorbidities } = useClassifier();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CoMorbiditiesStepResult | null>(null);

  // Fetch on open
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setResult(null);
    classifyCoMorbidities({ medicalText, diagnoses, llmModelId })
      .then((res) => setResult(res))
      .catch((e) => setError(e.message || "Failed to reclassify co-morbidities"))
      .finally(() => setLoading(false));
  }, [open, medicalText, diagnoses, llmModelId, classifyCoMorbidities]);

  return (
    <div
      className={cn("fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30", !open && "hidden")}
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-zinc-900 rounded-lg shadow-lg max-w-[90vw] w-full max-h-[80vh] p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Reclassify Co-morbidities</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            x
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto" style={{ maxHeight: "60vh" }}>
          {loading && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <span className="text-muted-foreground">Reclassifying co-morbidities...</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="rounded-md border-2 border-red-500 bg-red-50 p-4 w-full">
                <div className="font-medium text-red-800 mb-1">Error</div>
                <div className="text-sm text-red-600 whitespace-pre-wrap break-words">{error}</div>
              </div>
            </div>
          )}
          {result && !error && !loading && (
            <div className="space-y-4">
              <div className="font-semibold mb-1">Co-morbidities</div>
              <div className="flex flex-wrap gap-2">
                {result.coMorbidities && result.coMorbidities.length > 0 ? (
                  result.coMorbidities.map((coMorbid) => (
                    <span
                      key={coMorbid.code}
                      className="inline-block bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 rounded-full px-3 py-1 text-sm font-medium"
                    >
                      {coMorbid.code} - {coMorbid.description}
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground">No co-morbidities found</span>
                )}
              </div>
              {result.debugInfo && (
                <div>
                  <div className="font-semibold mb-1">Debug Info</div>
                  <DebugInfoJsonViewer debugInfo={result.debugInfo} />
                </div>
              )}
              {result.prompt && (
                <div>
                  <div className="font-semibold mb-1">Prompt</div>
                  <pre className="text-sm bg-muted p-2 rounded whitespace-pre-wrap">{result.prompt}</pre>
                </div>
              )}
              {result.responseText && (
                <div>
                  <div className="font-semibold mb-1">Raw Output</div>
                  <pre className="text-sm bg-muted p-2 rounded whitespace-pre-wrap">
                    {typeof result.responseText === "string"
                      ? result.responseText
                      : JSON.stringify(result.responseText, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export function CoMorbiditiesStepResultCard({
  result,
  medicalText,
  diagnoses,
  llmModelId,
}: {
  result: CoMorbiditiesStepResult;
  medicalText: string | null;
  diagnoses: string[];
  llmModelId?: LlmModelId | null;
}) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [reclassifyOpen, setReclassifyOpen] = useState(false);

  if (!result) return null;

  return (
    <Card className="rounded-md border bg-muted/30">
      <CardHeader>
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 w-full text-left focus:outline-none"
            onClick={() => setExpanded((prev) => !prev)}
            type="button"
          >
            {expanded ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
            <span className="font-medium">Co-morbidities Step Result</span>
          </button>
          <ReclassifyCoMorbiditiesDialog
            open={reclassifyOpen}
            onClose={() => setReclassifyOpen(false)}
            medicalText={medicalText}
            diagnoses={diagnoses}
            llmModelId={llmModelId}
          />
          {result.prompt && (
            <>
              <Button variant="ghost" onClick={() => setPromptOpen(true)} title="View Prompt">
                <FileText className="h-4 w-4 mr-1" />
                Prompt
              </Button>
              <PromptDialog
                open={promptOpen}
                onClose={() => setPromptOpen(false)}
                prompt={result.prompt}
                rawOutput={result.responseText}
              />
            </>
          )}
          <Button variant="ghost" onClick={() => setReclassifyOpen(true)} title="Reclassify Co-morbidities">
            <Repeat className="h-4 w-4 mr-1" />
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {result.error && (
            <div className="rounded-md border-2 border-red-500 bg-red-50 p-4">
              <div className="font-medium text-red-800 mb-1">Error</div>
              <div className="text-sm text-red-600 whitespace-pre-wrap break-words">{result.error}</div>
            </div>
          )}
          <div>
            <div className="font-semibold mb-1">Co-morbidities</div>
            <div className="flex flex-wrap gap-2">
              {result.coMorbidities && result.coMorbidities.length > 0 ? (
                result.coMorbidities.map((coMorbid) => (
                  <span
                    key={coMorbid.code}
                    className="inline-block bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 rounded-full px-3 py-1 text-sm font-medium"
                  >
                    {coMorbid.code} - {coMorbid.description}
                  </span>
                ))
              ) : (
                <span className="text-muted-foreground">No co-morbidities found</span>
              )}
            </div>
          </div>
          {result.debugInfo && (
            <div>
              <div className="font-semibold mb-1">Debug Info</div>
              <DebugInfoJsonViewer debugInfo={result.debugInfo} />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default CoMorbiditiesStepResultCard;
